import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
import AdminSelect, { SelectOption } from '../../components/AdminSelect';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import LHDataTable from '../../../../components/LHDataTable';
import { t } from './i18n';
import { EquipmentLanguage } from './types';
import { ListLanguageToggle } from './EquipmentConfig';
import { RouteTypeItem } from './types';
import {
  fetchRouteTypes,
  createRouteType,
  updateRouteType,
  checkRouteTypeExists,
  toggleRouteTypeActive,
} from './perfectTruckApi';
import { useToast } from '../../../../components/LhToastContainer';

interface ErrorMap { [key: string]: string }

const DELIVERY_TYPE_OPTIONS: SelectOption[] = [
  { label: 'WHD / Transport', value: 'WHD' },
  { label: 'DSD', value: 'DSD' },
];

const DELIVERY_TYPE_LABELS: Record<'WHD' | 'DSD', string> = {
  WHD: 'WHD / Transport',
  DSD: 'DSD',
};

const RouteTypeConfig: React.FC<{ listLanguage?: EquipmentLanguage }> = ({ listLanguage }) => {
  const currentListLanguage = listLanguage ?? 'en';
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ routeType: '' });
  const [frForm, setFrForm] = useState({ routeType: '' });
  const [esForm, setEsForm] = useState({ routeType: '' });
  const [modalValidationError, setModalValidationError] = useState<string>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [items, setItems] = useState<RouteTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editItem, setEditItem] = useState<RouteTypeItem | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formRouteType, setFormRouteType] = useState('');
  const [formDeliveryType, setFormDeliveryType] = useState<'WHD' | 'DSD' | ''>('');

  const [errors, setErrors] = useState<ErrorMap>({});
  const [globalError, setGlobalError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) =>
    state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? ''
  );

  const loadItems = useCallback(async () => {
    try {
      console.debug('[RouteTypeConfig] loadItems called, listLanguage=', currentListLanguage);
      setIsLoading(true);
      setApiError('');
      // Load route types using current list language so API is called with language filter
      const data = await fetchRouteTypes(currentListLanguage);
      setItems(data);
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to load route types.');
    } finally {
      setIsLoading(false);
    }
  }, [listLanguage]);

  useEffect(() => { loadItems(); }, [listLanguage]);

  const validate = (routeType: string, deliveryType: 'WHD' | 'DSD' | '', currentId?: number, lang: EquipmentLanguage = 'en'): ErrorMap => {
    const errs: ErrorMap = {};
    if (!routeType.trim()) {
      errs.routeType = t(lang, 'validation.routeTypeRequired');
    } else if (routeType.length > 50) {
      errs.routeType = t(lang, 'validation.routeTypeLengthExceed');
    } else {
      const dup = items.find(
        i => i.id !== currentId && i.routeType.toLowerCase() === routeType.trim().toLowerCase(),
      );
      if (dup) {
        errs.routeType = t(lang, 'validation.routeTypeExists').replace('{{routeType}}', routeType.trim());
      }
    }
    if (!deliveryType) {
      errs.deliveryType = t(lang, 'validation.deliveryTypeRequired');
    }
    return errs;
  };

  const handleToggleActive = async (id: number) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    if (item.isActive) {
      const activeCount = items.filter(i => i.isActive).length;
      if (activeCount <= 1) {
        setGlobalError(t(currentListLanguage, 'validation.atLeastOneActive'));
        return;
      }
    }
    setGlobalError('');
    try {
      const updated = await toggleRouteTypeActive(id, !item.isActive, requestedBy);
      setItems(prev => prev.map(i => i.id === id ? updated : i));
      toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.route'), subtext: '', variant: 'success' });
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to toggle active status.');
    }
  };

  const openAdd = () => {
    setIsAdding(true);
    setEditItem(null);
    setFormRouteType('');
    setFormDeliveryType('');
    setEnForm({ routeType: '' }); setFrForm({ routeType: '' }); setEsForm({ routeType: '' });
    setModalLanguage('en'); setModalValidationError('');
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
  };

  const openEdit = (item: RouteTypeItem) => {
    setIsAdding(false);
    setEditItem(item);
    setFormRouteType(item.routeType);
    setFormDeliveryType(item.deliveryType ?? '');
    setEnForm({ routeType: item.routeType }); setFrForm({ routeType: '' }); setEsForm({ routeType: '' });
    setModalLanguage('en'); setModalValidationError('');
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
    // Load translations for this routeType across languages
    (async () => {
      try {
        setModalLoading(true);
        const [enRes, frRes, esRes] = await Promise.all([
          fetchRouteTypes('en').catch(() => []),
          fetchRouteTypes('fr').catch(() => []),
          fetchRouteTypes('es').catch(() => []),
        ]);
        const findById = (arr: RouteTypeItem[]) => arr.find(r => r.id === item.id);
        const en = findById(enRes as RouteTypeItem[]);
        const fr = findById(frRes as RouteTypeItem[]);
        const es = findById(esRes as RouteTypeItem[]);
        setEnForm({ routeType: en?.routeType ?? item.routeType });
        setFrForm({ routeType: fr?.routeType ?? '' });
        setEsForm({ routeType: es?.routeType ?? '' });
      } catch (e) {
        // ignore
      } finally {
        setModalLoading(false);
      }
    })();
  };

  const doClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      setIsAdding(false);
      setEditItem(null);
      setFormRouteType('');
      setFormDeliveryType('');
      setEnForm({ routeType: '' }); setFrForm({ routeType: '' }); setEsForm({ routeType: '' });
      setModalLanguage('en'); setModalValidationError('');
      setErrors({});
      setHasUnsavedChanges(false);
    }
  };

  const confirmClose = () => {
    setIsAdding(false);
    setEditItem(null);
    setFormRouteType('');
    setFormDeliveryType('');
    setEnForm({ routeType: '' }); setFrForm({ routeType: '' }); setEsForm({ routeType: '' });
    setModalLanguage('en'); setModalValidationError('');
    setErrors({});
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
  };

  const handleSave = async () => {
    const englishValue = (enForm.routeType ?? '').trim();
    const errs = validate(englishValue, formDeliveryType, editItem?.id, modalLanguage);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    // Validate translations
    const translations = { en: enForm, fr: frForm, es: esForm } as any;
    const missing: string[] = [];
    (['en','fr','es'] as EquipmentLanguage[]).forEach(lang => {
      const tform = translations[lang];
      if (!tform || !tform.routeType || !tform.routeType.trim()) missing.push(lang);
    });
    if (missing.length > 0) {
      setModalValidationError(t(modalLanguage, 'validation.completeTranslations'));
      return;
    }
    const deliveryType = formDeliveryType as 'WHD' | 'DSD';
    try {
      setIsSaving(true);
      if (editItem) {
        // Build multilingual payloads with delivery type
        const common = {
          deliveryType,
          displayOrder: editItem.displayOrder,
          locationType: editItem.locationType,
          organizationType: editItem.organizationType,
          is3pl: editItem.is3pl,
          isActive: editItem.isActive,
          requestedBy,
        } as any;
        const make = (lang: EquipmentLanguage, f: any) => ({
          routeType: (f.routeType ?? '').trim(),
          deliveryType: common.deliveryType,
          displayOrder: common.displayOrder,
          locationType: common.locationType,
          organizationType: common.organizationType,
          is3pl: common.is3pl,
          isActive: common.isActive,
          requestedBy: common.requestedBy,
          language: lang,
        });
        const payloadArray = [make('en', enForm), make('fr', frForm), make('es', esForm)];
        const updated = await updateRouteType(editItem.id, payloadArray as any);
        setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.route'), subtext: '', variant: 'success' });
      } else {
        const exists = await checkRouteTypeExists(englishValue);
        if (exists) {
          setErrors({ routeType: t(modalLanguage, 'validation.routeTypeAlreadyExists') });
          setIsSaving(false);
          return;
        }
        const nextDisplayOrder = items.length > 0
          ? Math.max(...items.map(i => i.displayOrder)) + 1
          : 1;
        const common = { deliveryType, displayOrder: nextDisplayOrder, locationType: null, organizationType: null, is3pl: false, isActive: true, requestedBy } as any;
        const make = (lang: EquipmentLanguage, f: any) => ({
          routeType: (f.routeType ?? '').trim(),
          deliveryType: common.deliveryType,
          displayOrder: common.displayOrder,
          locationType: common.locationType,
          organizationType: common.organizationType,
          is3pl: common.is3pl,
          isActive: common.isActive,
          requestedBy: common.requestedBy,
          language: lang,
        });
        const payloadArray = [make('en', enForm), make('fr', frForm), make('es', esForm)];
        const created = await createRouteType(payloadArray as any);
        setItems(prev => [...prev, created]);
        toast.showToast({ header: t(currentListLanguage, 'toast.addSuccess.route'), subtext: '', variant: 'success' });
      }
      confirmClose();
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to save route type.');
    } finally {
      setIsSaving(false);
    }
  };

  const sorted = [...items].sort((a, b) => a.displayOrder - b.displayOrder);
  const filtered = searchQuery.trim()
    ? sorted.filter(i => i.routeType.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : sorted;

  return (
    <View>
      {/* Info banner */}
      <View style={styles.infoBanner}>
        <MaterialIcons name="check" size={16} color={AdminColors.blue700} style={styles.infoIcon} />
        <Text style={styles.infoText}>{t(currentListLanguage, 'info.routeBanner')}</Text>
      </View>

      {/* Toolbar: search + add */}
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <MaterialIcons name="search" size={16} color={AdminColors.textMuted} style={{ marginRight: 6 }} />
          <TextInput
            style={styles.searchInput}
            placeholder={t(currentListLanguage, 'searchPlaceholder')}
            placeholderTextColor={AdminColors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7}>
              <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        {!isAdding && !editItem && (
          <AdminButton
            label={t(currentListLanguage, 'addButton.route')}
            variant="primary"
            small
            onPress={openAdd}
          />
        )}
      </View>

      {(globalError || apiError) ? (
        <Text style={styles.globalError}>{globalError || apiError}</Text>
      ) : null}

      {(isAdding || editItem) && (
        <View style={styles.inlineForm}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={styles.fieldLabel}>{t(modalLanguage, 'form.routeTypeName')} <Text style={{ color: AdminColors.red400 }}>*</Text></Text>
            <View style={{ marginLeft: 12 }}>
              <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setModalValidationError(''); }} compact />
            </View>
          </View>

          <AdminFormField label="" error={errors.routeType || modalValidationError}>
            <AdminTextInput
              value={(modalLanguage === 'en' ? enForm.routeType : modalLanguage === 'fr' ? frForm.routeType : esForm.routeType)}
              onChangeText={v => {
                if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, routeType: v }));
                else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, routeType: v }));
                else setEsForm(prev => ({ ...prev, routeType: v }));
                setErrors({}); setHasUnsavedChanges(true); setModalValidationError('');
              }}
              placeholder={t(modalLanguage, 'placeholder.example')}
              hasError={!!errors.routeType || !!modalValidationError}
            />
          </AdminFormField>

          <AdminFormField label={t(modalLanguage, 'form.deliveryType')} required error={errors.deliveryType}>
            <AdminSelect
              value={formDeliveryType || undefined}
              onChange={value => {
                setFormDeliveryType(value as 'WHD' | 'DSD');
                setErrors(prev => ({ ...prev, deliveryType: '' }));
                setHasUnsavedChanges(true);
              }}
              options={DELIVERY_TYPE_OPTIONS}
              placeholder={t(modalLanguage, 'placeholder.selectDeliveryType')}
            />
          </AdminFormField>

          <View style={styles.inlineFormActions}>
            <AdminButton label={t(modalLanguage, 'button.cancel') ?? 'Cancel'} variant="secondary" onPress={doClose} />
            <AdminButton
              label={editItem ? (t(modalLanguage, 'button.saveChanges') ?? 'Update') : (t(modalLanguage, 'button.save') ?? 'Save')}
              variant="primary"
              onPress={handleSave}
              disabled={isSaving}
            />
          </View>
        </View>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      <AdminModal
        visible={showConfirmClose}
        title={t(modalLanguage, 'modal.unsavedTitle')}
        onClose={() => setShowConfirmClose(false)}
        footer={
          <>
            <AdminButton label={t(modalLanguage, 'modal.unsaved.continue')} variant="secondary" onPress={() => setShowConfirmClose(false)} />
            <AdminButton label={t(modalLanguage, 'modal.unsaved.close')} variant="primary" onPress={confirmClose} />
          </>
        }
      >
        <Text style={styles.confirmText}>{t(modalLanguage, 'modal.unsaved.message')}</Text>
      </AdminModal>

      {isLoading ? (
        <View style={styles.loadingWrapper}>
          <ActivityIndicator size="large" color={AdminColors.blue700} />
        </View>
      ) : (
        <View style={styles.surface}>
          <LHDataTable
            data={filtered}
            columns={[
              { columnKey: 'routeType', header: t(currentListLanguage, 'table.routeType'), flexGrow: 1, sortable: true },
              {
                columnKey: 'deliveryType',
                header: t(currentListLanguage, 'table.deliveryType') || 'Delivery Type',
                width: 180,
                sortable: false,
                renderCell: (item: any) => (
                  <Text style={styles.deliveryTypeText}>
                    {item.deliveryType ? DELIVERY_TYPE_LABELS[item.deliveryType as 'WHD' | 'DSD'] : '-'}
                  </Text>
                ),
              },
              {
                columnKey: 'isActive',
                header: t(currentListLanguage, 'table.active'),
                width: 120,
                sortable: false,
                renderCell: (item: any) => (
                  <TouchableOpacity onPress={() => handleToggleActive(item.id)} activeOpacity={0.7}>
                    <View style={[styles.statusBadge, item.isActive ? styles.statusActive : styles.statusInactive]}>
                      <Text style={[styles.statusBadgeText, item.isActive ? styles.statusActiveText : styles.statusInactiveText]}>
                        {item.isActive ? t(currentListLanguage, 'counts.active') : t(currentListLanguage, 'counts.inactive')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ),
              },
            ]}
            tableHeader=""
            tableDescription={`Showing ${filtered.length} of ${items.length} Route Types (${items.filter(i => i.isActive).length} ${t(currentListLanguage, 'counts.active')}, min 1 required)`}
            showHeaderTopBorder={false}
            onRowClick={(item) => openEdit(item as RouteTypeItem)}
            disableRowHighlight
            locale={{
              rowsPerPageLabel: t(currentListLanguage, 'pagination.rowsPerPage'),
              pageLabel: t(currentListLanguage, 'pagination.page'),
              pageOfLabel: t(currentListLanguage, 'pagination.of'),
              firstPageLabel: t(currentListLanguage, 'pagination.first'),
              prevPageLabel: t(currentListLanguage, 'pagination.previous'),
              nextPageLabel: t(currentListLanguage, 'pagination.next'),
              lastPageLabel: t(currentListLanguage, 'pagination.last'),
            }}
          />
        </View>
      )}


    </View>
  );
};

const styles = StyleSheet.create({
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.blue50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 12,
    marginBottom: 4,
  },
  infoIcon: {
    marginRight: 8,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    lineHeight: 20,
  },
  addButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    marginBottom: 4,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 4,
    gap: 12,
    flexWrap: 'wrap',
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    minWidth: 200,
    flex: 1,
    maxWidth: 300,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    outlineWidth: 0,
  } as any,
  loadingWrapper: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  globalError: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.red400,
    backgroundColor: AdminColors.red75,
    padding: 12,
    marginBottom: 8,
    borderRadius: 8,
  },
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { shadowColor: '#005EC0', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24, elevation: 2 },
    }),
    overflow: 'hidden',
    marginTop: 12,
  },
  inlineForm: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    padding: 16,
    marginBottom: 12,
  },
  fieldLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    color: AdminColors.textPrimary,
  },
  inlineFormActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusActive: {
    backgroundColor: AdminColors.green75,
  },
  statusInactive: {
    backgroundColor: AdminColors.gray100 ?? '#EBEBEB',
  },
  statusBadgeText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
  },
  statusActiveText: {
    color: AdminColors.green600,
  },
  statusInactiveText: {
    color: AdminColors.textMuted ?? '#737373',
  },
  confirmText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    marginBottom: 8,
  },
  deliveryTypeText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
});

export default RouteTypeConfig;